import { Component, EventEmitter, OnInit } from '@angular/core';
import { ModificationService } from '../modification.service';
import { Modification } from '../modification';
import { NotificationService } from '../../../shared/notification.service';

@Component({
  selector: 'app-modification-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class ModificationListComponent implements OnInit {
  public modification: Modification;

  /**
   * A list of all the modifications currently loaded.
   *
   * @type {Modification[]}
   */
  public modifications: Modification[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of modifications.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of modifications that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of modifications that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a modification has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public modificationDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {ModificationService} modificationService
   * @param {NotificationService} notificationService
   */
  constructor(
    private modificationService: ModificationService,
    private notificationService: NotificationService,
  ) {
    this.modificationService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.modificationService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.modificationService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.modificationService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the modification list.
   */
  public ngOnInit(): void {
    this.loadModifications(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * modification list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadModifications(pageNumber);
  }

  /**
   * Loads a list of modifications using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadModifications(page?: number, limit?: number): void {
    const listModifications = this.modificationService
      .listModifications(page, limit)
      .subscribe((modifications: Modification[]) => {
        this.modifications = modifications;
        listModifications.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate modification ID
   * to delete the modification.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.modificationService.deleteModification(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Modification Deleted`,
          `Modification with ID ${id} was deleted.`,
          5,
        );
        this.modificationDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Modification Not Deleted`,
          err.error.message,
          10,
        );
        this.modificationDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
